import { useMemo } from "react";
import { CloudRecordingUsageResponse } from "@switcherstudio/switcher-api-client";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { useCloudVideosLimitMode } from "hooks/useCloudVideosLimitMode";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { UploadedFile } from "store/videoUploadSession/types";

/** Resolves used/max in the active unit: videos count, or seconds in minutes mode */
export const useCloudVideosUsage = (
    cloudUsage: CloudRecordingUsageResponse | undefined
) => {
    const { isMinutesMode, claimMaxSeconds } = useCloudVideosLimitMode();

    return useMemo(() => {
        if (!cloudUsage) {
            return {
                isMinutesMode,
                total: undefined as number | undefined,
                max: undefined as number | undefined,
                isFull: undefined as boolean | undefined
            };
        }
        const total = isMinutesMode
            ? (cloudUsage.TotalDurationSeconds ?? 0)
            : cloudUsage.TotalRecordings;
        const max = isMinutesMode
            ? cloudUsage.MaxDurationSeconds || claimMaxSeconds
            : cloudUsage.MaxRecordings;
        return { isMinutesMode, total, max, isFull: total >= max };
    }, [cloudUsage, isMinutesMode, claimMaxSeconds]);
};

/** Returns some values regarding current cloud usage in relation to added files */
export const useGetCloudUsageForUploadSessions = () => {
    const { uploadSessions } = useSelector(
        (s: RootState) => s.videoUploadSession
    );

    // all of the added files from all of the upload sessions that haven't started uploading yet
    const aggregatedSessionFiles: UploadedFile[] = useMemo(() => {
        return Object.values(uploadSessions)
            .reduce(
                (aggregatedFiles, currentSession) => [
                    ...aggregatedFiles,
                    ...Object.values(currentSession.files ?? {})
                ],
                [] as UploadedFile[]
            )
            .filter((file) => file.isAwaitingUpload || file.isUploading);
    }, [uploadSessions]);

    const {
        data: cloudUsage,
        dispatchApiRequest: updateUsage,
        loading: cloudUsageLoading
    } = useSwitcherClient((client) => client.cloudRecordings_GetUsage, {
        suppressErrorBoundary: true,
        requestImmediately: true
    });

    const {
        isMinutesMode,
        total: cloudTotal,
        max: cloudMax,
        isFull: libraryFull
    } = useCloudVideosUsage(cloudUsage);

    const addedVideosExceedCloudUsage = useMemo(() => {
        if (!aggregatedSessionFiles || !cloudUsage) return false;
        // new uploads have no known duration, so only block once the library is full
        if (isMinutesMode) {
            return (
                !!libraryFull &&
                aggregatedSessionFiles.some((file) => file.isAwaitingUpload)
            );
        }
        if (libraryFull) return false;
        if (
            aggregatedSessionFiles.length + cloudUsage.TotalRecordings >
            cloudUsage.MaxRecordings
        ) {
            return true;
        }
        return false;
    }, [libraryFull, isMinutesMode, aggregatedSessionFiles, cloudUsage]);

    return {
        cloudUsageLoading,
        libraryFull,
        addedVideosExceedCloudUsage,
        cloudTotal,
        cloudMax,
        videoCount: cloudUsage?.TotalRecordings,
        isMinutesMode,
        updateUsage
    };
};
